import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import type { PdfTableExportOptions, PdfLogoMeta } from "./pdfExport.types";
import {
  buildColumnStyles,
  DEFAULT_HEADER_FILL_COLOR,
  drawPageHeader,
  loadLogoMeta,
  LOGO_TABLE_GAP,
  resolveColumnAlignments,
  resolveColumnWidths,
  safeFormatCellValue,
  validateExportInput,
} from "./pdfExport.helpers";

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULTS = {
  fileName: "tabla",
  orientation: "portrait",
  unit: "mm",
  format: "a4",
  fontSize: 8,
  cellPadding: 2,
  horizontalMargin: 25,
  logoWidth: 30,
  marginBottom: 10,
  titleFontSize: 10,
  borderWidth: 0.15,
} as const;

// ─── Main export function ─────────────────────────────────────────────────────

/**
 * Generic, framework-agnostic helper to export tabular data to a PDF file.
 *
 * Behaviour
 * ---------
 * - The logo (when provided) and title are drawn on **every page** via
 *   `didDrawPage`, not just the first.
 * - Column widths are normalized to fill the available table area proportionally.
 * - If `onError` is provided, all errors are routed there and the function
 *   returns silently; otherwise they are re-thrown.
 * - If the logo URL fails to load, the export continues without the logo.
 */
export function exportTableToPdf<T>(options: PdfTableExportOptions<T>): Promise<void> {
  const {
    columns,
    rows,
    fileName = DEFAULTS.fileName,
    title,
    orientation = DEFAULTS.orientation,
    unit = DEFAULTS.unit,
    format = DEFAULTS.format,
    onSuccess,
    onError,
    headerFillColor = DEFAULT_HEADER_FILL_COLOR,
    logoUrl,
    logoWidth = DEFAULTS.logoWidth,
    logoHeight,
    columnWidths,
    columnAlignments,
    fontSize = DEFAULTS.fontSize,
    cellPadding = DEFAULTS.cellPadding,
    horizontalMargin = DEFAULTS.horizontalMargin,
    titleFontSize = DEFAULTS.titleFontSize,
    borderWidth = DEFAULTS.borderWidth,
    headerCellPadding,
    bodyValign,
  } = options;

  // ── Input validation ────────────────────────────────────────────────────────
  const validation = validateExportInput(columns, rows);
  if (!validation.ok) {
    if (onError) {
      onError(validation.error);
      return Promise.resolve();
    }
    return Promise.reject(validation.error);
  }

  // ── Build static table data ─────────────────────────────────────────────────
  const tableHead = [columns.map((col) => col.header)];
  const tableBody = rows.map((row) =>
    columns.map((col) => safeFormatCellValue(col.accessor(row)))
  );

  const effectiveWidths = resolveColumnWidths(columns, columnWidths);
  const effectiveAlignments = resolveColumnAlignments(columns, columnAlignments);

  // ── Core rendering ──────────────────────────────────────────────────────────
  const renderPdf = (logoMeta?: PdfLogoMeta): void => {
    const doc = new jsPDF({ orientation, unit, format, compress: true });

    const margin = {
      top: logoMeta
        ? Math.ceil(logoMeta.y + logoMeta.height) + LOGO_TABLE_GAP
        : title ? 16 : 10,
      left: horizontalMargin,
      right: horizontalMargin,
      bottom: DEFAULTS.marginBottom,
    };

    const availableWidth =
      doc.internal.pageSize.getWidth() - margin.left - margin.right;

    const columnStyles = buildColumnStyles(
      columns,
      effectiveWidths,
      effectiveAlignments,
      availableWidth
    );

    autoTable(doc, {
      head: tableHead,
      ...(tableBody.length > 0 && { body: tableBody }),
      styles: {
        fontSize,
        cellPadding,
        lineWidth: borderWidth,
        lineColor: [0, 0, 0],
        font: "helvetica",
      },
      headStyles: {
        fillColor: headerFillColor,
        textColor: [0, 0, 0],
        halign: "center",
        valign: "middle",
        ...(headerCellPadding !== undefined && { cellPadding: headerCellPadding }),
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        ...(bodyValign && {
          valign:
            bodyValign === "TOP"
              ? "top"
              : bodyValign === "MIDDLE"
                ? "middle"
                : "bottom",
        }),
      },
      theme: "grid",
      tableWidth: availableWidth,
      columnStyles,
      margin,
      rowPageBreak: "avoid",
      didDrawPage: () => drawPageHeader(doc, logoMeta, title, titleFontSize),
    });

    doc.save(`${fileName.trim() || DEFAULTS.fileName}.pdf`);
    onSuccess?.();
  };

  // ── Logo loading + render ───────────────────────────────────────────────────
  const handleError = (err: unknown): void => {
    if (onError) {
      onError(err);
    } else {
      throw err;
    }
  };

  if (logoUrl) {
    const measureDoc = new jsPDF({ orientation, unit, format });
    measureDoc.setFont("helvetica", "bold");
    measureDoc.setFontSize(titleFontSize);

    return loadLogoMeta(measureDoc, logoUrl, title, logoWidth, logoHeight)
      .then((logoMeta) => {
        renderPdf(logoMeta);
      })
      .catch((loadErr) => {
        console.warn(
          "PDF export: logo failed to load, continuing without it.",
          loadErr
        );
        renderPdf();
      })
      .catch((err) => {
        handleError(err);
      });
  }

  try {
    renderPdf();
    return Promise.resolve();
  } catch (err) {
    handleError(err);
    return Promise.resolve();
  }
}
